import { styled } from "goober";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { StreamMessage } from "../../hooks/useStreamingText";
import { formatCompactDuration } from "../../lib/formatCompactDuration";
import { ActivityAnimationContext } from "../ActivityAnimationContext";
import { Icon } from "../Icon";
import { PulsingIcon } from "../PulsingIcon";
import { ShimmerSpan } from "../ShimmerSpan";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { WavyLine } from "../WavyLine";
import type { ChatTool } from "./ChatTool";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandWrapper,
} from "./Expand";
import { activityDuration } from "./activityDuration";
import { ActivityRow } from "./ActivityRow";
import { AssistantMessage } from "./AssistantMessage";
import { Reasoning } from "./Reasoning";
import { ToolUse } from "./ToolUse";
import { useLiveActivity } from "./useLiveActivity";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
`;

const ToolsContainer = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const GroupExpandInner = styled("div")`
  overflow: hidden;
  min-height: 0;
  content-visibility: hidden;
  &[data-expanded="true"] {
    content-visibility: visible;
  }
`;

type Props = {
  entries: ChatEntryWithSubTables[];
  activePromptId?: string;
  collapsed: boolean;
  toggleCollapsed: () => void;
  expandedToolUse: string | null;
  setExpandedToolUse: (id: string | null) => void;
  tools: ReadonlyMap<string, ChatTool>;
  isTrailing?: boolean;
  responseStarted?: boolean;
  onProvideResult?: (
    entryId: string,
    output: unknown,
    isError: boolean
  ) => void;
  streamingEntries?: ReadonlyMap<string, StreamMessage>;
};

export function ActivityEntry({
  entry,
  live,
  connected,
  hasNext,
  stream,
  expandedToolUse,
  setExpandedToolUse,
  tools,
  interrupted,
  onProvideResult,
  onExpandHistory,
}: Pick<
  Props,
  "expandedToolUse" | "setExpandedToolUse" | "tools" | "onProvideResult"
> & {
  entry: ChatEntryWithSubTables;
  live: boolean;
  connected: boolean;
  hasNext: boolean;
  stream: StreamMessage | undefined;
  interrupted: boolean;
  onExpandHistory?: () => void;
}) {
  if (entry.entryType === "AssistantMessage") {
    return (
      <ActivityRow
        connected={connected}
        hasNext={hasNext}
        variant={hasNext ? "through" : "terminate"}
      >
        <div data-chat-entry-id={entry.id} style={{ padding: "8px 8px 12px" }}>
          <AssistantMessage entry={entry} stream={stream} nested />
        </div>
      </ActivityRow>
    );
  }
  return (
    <ActivityAnimationContext
      value={{
        live,
        activityId: `${entry.id}:${entry.toolCall?.completedAt ?? "pending"}`,
      }}
    >
      {entry.assistantReasoning ? (
        <Reasoning
          entry={entry}
          live={live}
          connected={connected}
          hasNext={hasNext}
          stream={stream}
          onExpandHistory={onExpandHistory}
        />
      ) : (
        <ToolUse
          toolUse={{
            id: entry.id,
            toolName: entry.toolCall!.toolName,
            input:
              (entry.streaming ? stream?.toolInput : undefined) ??
              entry.toolCall!.input,
            output: entry.toolCall!.output,
            isError: entry.toolCall!.isError,
          }}
          result={
            entry.toolCall!.completedAt != null
              ? {
                  id: entry.id,
                  output: entry.toolCall!.output,
                  isError: entry.toolCall!.isError ?? false,
                }
              : null
          }
          expanded={expandedToolUse === entry.id}
          toggleExpanded={() =>
            setExpandedToolUse(expandedToolUse === entry.id ? null : entry.id)
          }
          toolAfter={hasNext}
          connected={connected}
          tool={tools.get(entry.toolCall!.toolName)}
          createdAt={entry.createdAt}
          completedAt={entry.toolCall!.completedAt}
          interrupted={interrupted}
          onProvideResult={onProvideResult}
          onExpandHistory={onExpandHistory}
        />
      )}
    </ActivityAnimationContext>
  );
}

export function ToolUseGroup({
  entries,
  activePromptId,
  collapsed,
  toggleCollapsed,
  expandedToolUse,
  setExpandedToolUse,
  tools,
  isTrailing,
  responseStarted,
  onProvideResult,
  streamingEntries,
}: Props) {
  const latest = entries.findLast(
    (entry) => entry.entryType !== "AssistantMessage"
  );
  const pendingTool = entries.findLast(
    (entry) => entry.toolCall && entry.toolCall.completedAt == null
  );
  const currentEntry =
    entries.find((entry) => entry.id === activePromptId) ??
    (responseStarted
      ? pendingTool
      : latest?.assistantReasoning
        ? latest
        : (pendingTool ?? latest));
  const compact = !!isTrailing && collapsed && !!currentEntry;
  const { displayed, fading, activityRef } = useLiveActivity(
    currentEntry,
    currentEntry ? streamingEntries?.get(currentEntry.id) : undefined,
    compact
  );
  const shared = {
    expandedToolUse,
    setExpandedToolUse,
    tools,
    onProvideResult,
  };
  if (!entries.length) return null;

  return (
    <Container data-live-tool-use={isTrailing || undefined}>
      {compact && displayed.entry ? (
        <div ref={activityRef} inert={fading}>
          <ActivityEntry
            {...shared}
            entry={displayed.entry}
            live
            connected={false}
            hasNext={false}
            stream={displayed.stream}
            interrupted={false}
            onExpandHistory={toggleCollapsed}
          />
        </div>
      ) : (
        <CollapsibleHeader aria-expanded={!collapsed} onPress={toggleCollapsed}>
          <CollapsibleHeaderInner>
            <ToggleIconContainer aria-hidden>
              <DefaultIcon>
                {isTrailing ? (
                  <PulsingIcon data-pending>
                    <WavyLine
                      size={12}
                      color="var(--text-secondary)"
                      opacity={1}
                    />
                  </PulsingIcon>
                ) : (
                  <Icon
                    name="toolbox"
                    size={12}
                    color="var(--text-secondary)"
                  />
                )}
              </DefaultIcon>
              <HoverIcon>
                <Icon
                  name={collapsed ? "plus" : "minus"}
                  size={12}
                  color="var(--text-secondary)"
                />
              </HoverIcon>
            </ToggleIconContainer>
            <Text variant="text-small" color="var(--text-secondary)">
              <ShimmerSpan data-pending={isTrailing}>
                {isTrailing
                  ? "Working"
                  : `Worked for ${formatCompactDuration(activityDuration(entries))}`}
              </ShimmerSpan>
            </Text>
          </CollapsibleHeaderInner>
        </CollapsibleHeader>
      )}
      <ExpandWrapper data-expanded={!collapsed}>
        <GroupExpandInner data-expanded={!collapsed} inert={collapsed}>
          <ToolsContainer>
            {(!collapsed || !isTrailing) &&
              entries.map((entry, index) => (
                <ActivityEntry
                  {...shared}
                  key={entry.id}
                  entry={entry}
                  live={!!isTrailing && entry.id === currentEntry?.id}
                  connected
                  hasNext={index < entries.length - 1}
                  stream={streamingEntries?.get(entry.id)}
                  interrupted={
                    !isTrailing && entry.toolCall?.completedAt == null
                  }
                />
              ))}
          </ToolsContainer>
        </GroupExpandInner>
      </ExpandWrapper>
    </Container>
  );
}
